import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function PTSDEducation() {
  const { t } = useTranslation();
  const [openAccordion, setOpenAccordion] = useState<number | null>(null);

  const accordionItems = [
    {
      title: t('ptsd_q1_title'),
      content: t('ptsd_q1_content')
    },
    {
      title: t('ptsd_q2_title'),
      content: t('ptsd_q2_content')
    },
    {
      title: t('ptsd_q3_title'),
      content: t('ptsd_q3_content')
    },
    {
      title: t('ptsd_q4_title'),
      content: t('ptsd_q4_content')
    },
    {
      title: t('ptsd_q5_title'),
      content: t('ptsd_q5_content')
    },
    {
      title: t('ptsd_q6_title'),
      content: t('ptsd_q6_content')
    },
    {
      title: t('ptsd_q7_title'),
      content: t('ptsd_q7_content')
    },
    {
      title: t('ptsd_q8_title'),
      content: t('ptsd_q8_content')
    }
  ];

  const resources = [
    {
      title: t('ptsd_resource1_title'),
      description: t('ptsd_resource1_desc'),
      icon: 'ri-book-open-line'
    },
    {
      title: t('ptsd_resource2_title'),
      description: t('ptsd_resource2_desc'),
      icon: 'ri-phone-line'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-b from-stone-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-medium text-stone-800 mb-6">{t('ptsd_hero_title')}</h1>
          <p className="text-lg text-stone-600 leading-relaxed max-w-2xl mx-auto">
            {t('ptsd_hero_desc')}
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-medium text-stone-800 mb-6">{t('ptsd_education_title')}</h2>
              <p className="text-stone-600 leading-relaxed mb-6">
                {t('ptsd_education_p1')}
              </p>
              <p className="text-stone-600 leading-relaxed">
                {t('ptsd_education_p2')}
              </p>
            </div>
            <div>
              <img
                src="https://static.readdy.ai/image/e7605311edff4188dbd664db0f9809d3/0c173615a6bcc6a5616820ea0f9413a5.png"
                alt="Veteran in peaceful study"
                className="w-full rounded-lg shadow-sm"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Accordion Section */}
      <section className="py-16 bg-stone-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-medium text-stone-800 mb-6">{t('ptsd_questions_title')}</h2>
            <p className="text-stone-600 leading-relaxed">
              {t('ptsd_questions_desc')}
            </p>
          </div>

          <div className="space-y-4">
            {accordionItems.map((item, index) => (
              <div key={index} className="bg-white rounded-lg border border-stone-200 overflow-hidden">
                <button
                  onClick={() => setOpenAccordion(openAccordion === index ? null : index)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-stone-50 transition-colors duration-200"
                >
                  <h3 className="text-lg font-semibold text-stone-800">{item.title}</h3>
                  <i className={`ri-${openAccordion === index ? 'subtract' : 'add'}-line text-xl text-amber-600 transition-transform duration-200`}></i>
                </button>
                {openAccordion === index && (
                  <div className="px-6 pb-6 border-t border-amber-100">
                    <p className="text-stone-600 leading-relaxed pt-4">{item.content}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Principles */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-medium text-stone-800 mb-6">{t('ptsd_principles_title')}</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-amber-500/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                <i className="ri-heart-line text-2xl text-amber-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-stone-800 mb-3">{t('ptsd_principle1_title')}</h3>
              <p className="text-stone-600 leading-relaxed">
                {t('ptsd_principle1_desc')}
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-amber-500/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                <i className="ri-time-line text-2xl text-amber-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-stone-800 mb-3">{t('ptsd_principle2_title')}</h3>
              <p className="text-stone-600 leading-relaxed">
                {t('ptsd_principle2_desc')}
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-amber-500/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                <i className="ri-team-line text-2xl text-amber-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-stone-800 mb-3">{t('ptsd_principle3_title')}</h3>
              <p className="text-stone-600 leading-relaxed">
                {t('ptsd_principle3_desc')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Resources Section */}
      <section className="py-16 bg-stone-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-medium text-stone-800 mb-6">{t('ptsd_resources_title')}</h2>
            <p className="text-stone-600 leading-relaxed">
              {t('ptsd_resources_desc')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {resources.map((resource, index) => (
              <div key={index} className="bg-white rounded-lg p-8 shadow-sm">
                <div className="w-14 h-14 bg-amber-500/10 rounded-lg flex items-center justify-center mb-6 mx-auto">
                  <i className={`${resource.icon} text-2xl text-amber-600`}></i>
                </div>
                <h3 className="text-xl font-semibold text-stone-800 text-center mb-4">{resource.title}</h3>
                <p className="text-stone-600 leading-relaxed text-center mb-6">{resource.description}</p>
                <div className="text-center">
                  <button className="border border-amber-600 text-amber-600 px-6 py-3 rounded-full hover:bg-amber-600 hover:text-white transition-colors duration-300 whitespace-nowrap">
                    {t('ptsd_coming_soon')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-medium text-stone-800 mb-6">{t('ptsd_questions_cta_title')}</h2>
          <p className="text-lg text-stone-600 leading-relaxed mb-8 max-w-2xl mx-auto">
            {t('ptsd_questions_cta_desc')}
          </p>
          <a
            href="/contact"
            className="inline-block bg-stone-800 text-white px-8 py-4 rounded-full font-medium hover:bg-amber-600 transition-colors duration-300 whitespace-nowrap"
          >
            {t('ptsd_questions_cta_button')}
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}